import { userError } from "@/lib/userError";
import React, { useRef, useState } from "react";
import { Calendar, AlertTriangle, MapPin, Users, Plus, FileText, RefreshCw, Clock } from "lucide-react";
import { getCalendarAgenda, getCalendarOverview, syncCalendar, draftEvent, publishEvent, getIntegrations } from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { ContentSearch } from "@/components/ContentSearch";
import { matchesContent } from "@/lib/contentSearch";
import { PageHeader, LoadingCards, ErrorState, EmptyState, RefreshNotice } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { ApprovalButton } from "@/components/ApprovalButton";
import { fiTime, fiDate } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { DataFreshnessNotice } from "@/components/DataFreshnessNotice";
import { requestKeyFor, resetRequestKey } from "@/lib/idempotency";
import { defaultCalendarRange, shiftCalendarDate, validCalendarRange } from "@/lib/calendarRange";
import {getCalendarItem} from "@/lib/apiClient";
import {useLinkedRecord} from "@/lib/useLinkedRecord";
import {LinkedRecord} from "@/components/LinkedRecord";
import {CalendarTimeline} from "@/components/CalendarTimeline";
import {integrationFreshness,useFreshnessClock} from "@/lib/sourceFreshness";

const NewEventDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [location, setLocation] = useState("");
  const [reminderMinutes, setReminderMinutes] = useState("30");
  const [saving, setSaving] = useState(false);
  const formId = React.useId();
  const [formError, setFormError] = useState("");
  const [invalidField, setInvalidField] = useState("");
  const requestRef = useRef({ key: "", fingerprint: "" });

  const submit = async () => {
    if (!title.trim() || !start || !end) { setFormError("Täytä otsikko ja ajat."); setInvalidField(formId + "-field0"); document.getElementById(formId + "-field0")?.focus(); return; }
    if (new Date(end) <= new Date(start)) { setFormError("Päättymisajan pitää olla alkamisajan jälkeen."); setInvalidField(formId + "-field2"); document.getElementById(formId + "-field2")?.focus(); return; }
    setSaving(true);
    try {
      const payload = { title, start: new Date(start).toISOString(), end: new Date(end).toISOString(), location, reminder_minutes: Number(reminderMinutes) };
      await draftEvent({ ...payload, idempotency_key: requestKeyFor(requestRef, payload) });
      toast.success("Tapahtumaluonnos luotu — odottaa hyväksyntää.");
      resetRequestKey(requestRef);
      setOpen(false); setTitle(""); setStart(""); setEnd(""); setLocation(""); setReminderMinutes("30");
      await onCreated();
    } catch (requestError) {
      toast.error(userError(requestError, "Tapahtumaluonnoksen luominen epäonnistui."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid="new-event-btn" className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground active:scale-95 transition-transform">
          <Plus className="h-4 w-4" /> Uusi tapahtuma
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl max-w-sm mx-auto" data-testid="new-event-dialog">
        <DialogHeader><DialogTitle className="font-heading">Uusi tapahtumaluonnos</DialogTitle></DialogHeader>
        <form id={formId} onSubmit={(event) => { event.preventDefault(); setFormError(""); submit(); }} onChange={() => { setFormError(""); setInvalidField(""); }} onInvalidCapture={(event) => { event.preventDefault(); if (event.target === event.currentTarget.querySelector(":invalid")) { setInvalidField(event.target.id); setFormError("Tarkista kenttä: " + (event.target.labels?.[0]?.textContent || "pakollinen tieto")); event.target.focus(); } }} className="space-y-3">
          <div><Label htmlFor={formId + "-field0"} className="text-xs">Otsikko</Label><Input id={formId + "-field0"} aria-invalid={invalidField === formId + "-field0"} aria-describedby={formId + "-error"} required data-testid="event-title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl mt-1" /></div>
          <div><Label htmlFor={formId + "-field1"} className="text-xs">Alkaa</Label><Input id={formId + "-field1"} aria-invalid={invalidField === formId + "-field1"} aria-describedby={formId + "-error"} required data-testid="event-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl mt-1" /></div>
          <div><Label htmlFor={formId + "-field2"} className="text-xs">Päättyy</Label><Input id={formId + "-field2"} aria-invalid={invalidField === formId + "-field2"} aria-describedby={formId + "-error"} required data-testid="event-end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-xl mt-1" /></div>
          <div><Label htmlFor={formId + "-field3"} className="text-xs">Paikka</Label><Input id={formId + "-field3"} aria-invalid={invalidField === formId + "-field3"} aria-describedby={formId + "-error"} data-testid="event-location" value={location} onChange={(e) => setLocation(e.target.value)} className="rounded-xl mt-1" /></div>
          <div><Label htmlFor={formId + "-field4"} className="text-xs">Muistutus ennen tapahtumaa</Label><Select value={reminderMinutes} onValueChange={setReminderMinutes}><SelectTrigger id={formId + "-field4"} className="rounded-xl mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="0">Ei muistutusta</SelectItem><SelectItem value="10">10 minuuttia</SelectItem><SelectItem value="30">30 minuuttia</SelectItem><SelectItem value="60">1 tunti</SelectItem><SelectItem value="1440">1 päivä</SelectItem></SelectContent></Select></div>
        </form>
        <p id={formId + "-error"} role="alert" className="text-sm text-destructive">{formError}</p>
        <DialogFooter>
          <button type="submit" form={formId} data-testid="event-create-confirm" disabled={saving} className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
            {saving ? "Tallennetaan…" : "Luo luonnos"}
          </button>
        </DialogFooter>
        <p className="text-[11px] text-muted-foreground text-center">Luonnos tallennetaan ensin tähän sovellukseen. Google Kalenteriin lisääminen vaatii vielä erillisen hyväksynnän.</p>
      </DialogContent>
    </Dialog>
  );
};

export default function CalendarView() {
  const selection=useLinkedRecord("event",getCalendarItem,"calendar");
  const [query, setQuery] = useState("");
  const [range, setRange] = useState(()=>{const initial=defaultCalendarRange();return {...initial,end:shiftCalendarDate(initial.start,6)};});
  const [focusedId,setFocusedId]=useState(null);
  const {data:integrations}=useData(getIntegrations,[],["integrations"]);
  const freshnessNow=useFreshnessClock();
  const [rangeDraft, setRangeDraft] = useState(range);
  const validRange = validCalendarRange(rangeDraft);
  const { data: agenda, loading, error, refetch, refreshing, updatedAt } = useData(() => getCalendarAgenda(range.start, shiftCalendarDate(range.end,1)), [range.start,range.end], ["calendar"]);
  const data = agenda?.items;
  const selectedEvent=selection.data;
  const [prep, setPrep] = useState(null);
  const { data: overview, refetch: refreshOverview } = useData(getCalendarOverview, [], ["calendar"]);
  const [syncing, setSyncing] = useState(false);


  const refreshCalendar = async () => {
    setSyncing(true);
    try {
      const result = await syncCalendar();
      await Promise.all([refetch(), refreshOverview()]);
      toast.success(`${result.synced} tapahtumaa päivitetty. Kalenteria vain luettiin.`);
    } catch (requestError) {
      toast.error(userError(requestError, "Kalenterin päivittäminen epäonnistui."));
    } finally {
      setSyncing(false);
    }
  };

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const eventGroups = [
    { id: "today", label: "Tänään", events: (data || []).filter((event) => {
      const start = new Date(event.start);
      return new Date(event.end) > startOfToday && start < startOfTomorrow;
    }) },
    { id: "upcoming", label: "Tulevat", events: (data || []).filter((event) => new Date(event.start) >= startOfTomorrow) },
    { id: "past", label: "Menneet", events: (data || []).filter((event) => new Date(event.end) <= startOfToday) },
    { id: "unknown", label: "Aika puuttuu", events: (data || []).filter((event) => Number.isNaN(new Date(event.start).getTime())) },
  ].map((group) => ({ ...group, events: group.events.filter((event) => event.id!==selection.data?.id && (event.id===focusedId || query && matchesContent(query, [event.title, event.location]))) })).filter((group) => group.events.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader icon={Calendar} title="Kalenteri"
        action={<NewEventDialog onCreated={async () => { await refetch(); await refreshOverview(); }} />} />
      <DataFreshnessNotice integrationId="int-gcal" sourceLabel="Kalenteri" coverageComplete={(integrations||[]).find(row=>row.id==="int-gcal")?.sync_complete===true} />
      <details className="order-last rounded-2xl border border-border p-3"><summary className="min-h-11 cursor-pointer text-sm font-medium">Kalenterin asetukset ja ajanjakso</summary>
      <section className="mb-4 rounded-2xl border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-start gap-3">
          <Calendar className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Päivän ohjelma</p>
            <p className="mt-1 text-sm text-muted-foreground">{overview?.summary || "Ladataan päivän kalenteria…"}</p>
          </div>
        </div>
        {overview?.free_slots?.length > 0 && (
          <div className="mt-3">
            <p className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Vapaat ajat tallennetuissa tiedoissa 9–17</p>
            <div className="flex flex-wrap gap-2">
              {overview.free_slots.slice(0, 4).map((slot) => (
                <span key={`${slot.start}-${slot.end}`} className="rounded-full border border-border bg-background/50 px-3 py-1 text-xs text-foreground">
                  {fiTime(slot.start)}–{fiTime(slot.end)}
                </span>
              ))}
            </div>
          </div>
        )}
        <button type="button" onClick={refreshCalendar} disabled={syncing} className="mt-3 inline-flex items-center gap-2 rounded-full border border-border px-3 py-2 text-xs font-semibold disabled:opacity-50">
          <RefreshCw className={cn("h-3.5 w-3.5", syncing && "animate-spin")} />
          {syncing ? "Päivitetään…" : "Hae kalenteri"}
        </button>
        <p className="mt-2 text-[11px] text-muted-foreground">Kalenterin lukeminen ei muuta tietoja. Uusi tapahtuma lisätään vasta erillisellä hyväksynnällä.</p>
      </section>

      <form className="mb-4 space-y-3 rounded-xl border border-border p-3" onSubmit={event=>{event.preventDefault();if(validRange)setRange({...rangeDraft});}}>
        <p className="text-sm font-semibold">Näytettävä ajanjakso</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-muted-foreground">Alkupäivä<input aria-label="Kalenterin alkupäivä" type="date" required value={rangeDraft.start} onChange={event=>{const value=event.target.value;setRangeDraft(current=>({...current,start:value}));}} className="mt-1 min-h-11 w-full min-w-0 rounded-lg border border-border bg-background p-2" /></label>
          <label className="text-xs text-muted-foreground">Loppupäivä<input aria-label="Kalenterin loppupäivä" type="date" required value={rangeDraft.end} onChange={event=>{const value=event.target.value;setRangeDraft(current=>({...current,end:value}));}} className="mt-1 min-h-11 w-full min-w-0 rounded-lg border border-border bg-background p-2" /></label>
        </div>
        {!validRange && <p role="alert" className="text-xs text-destructive">Valitse alku- ja loppupäivä järjestyksessä, enintään 366 päivän jakso.</p>}
        <button type="submit" disabled={!validRange} className="min-h-11 rounded-full border border-border px-4 text-sm font-semibold disabled:opacity-50">Näytä ajanjakso</button>
        <p className="text-xs text-muted-foreground">Molemmat valitut päivät ovat mukana. Rajaus lukee vain sovellukseen tallennettuja tapahtumia, ei hae uusia Googlesta.</p>
      </form>
      {agenda && <p className="mb-3 text-xs text-muted-foreground">Näytetään {data.length} tapahtumaa ajalta {range.start}–{range.end}. Haku koskee vain tätä jaksoa ja ladattuja tapahtumia.</p>}
      </details>
      {agenda?.scope.has_more && <p role="status" className="mb-3 rounded-xl border border-amber-500/40 p-3 text-sm text-amber-500">Kaikkia jakson tapahtumia ei näytetä. Hakuraja täyttyi; valitse lyhyempi ajanjakso. Päällekkäisyystarkistus koskee vain näytettyjä tapahtumia.</p>}
      <LinkedRecord selection={selection} label="tapahtuma" path="/calendar">
        {selectedEvent && <div className="space-y-2" data-testid="selected-calendar-event">
          <h3 className="font-medium">{selectedEvent.title}</h3>
          <p className="text-sm">{fiDate(selectedEvent.start)} · {selectedEvent.all_day ? "Koko päivä" : `${fiTime(selectedEvent.start)}–${fiTime(selectedEvent.end)}`}</p>
          {selectedEvent.location && <p className="text-sm">{selectedEvent.location}</p>}
          <p className="text-xs text-muted-foreground">Linkistä avattu tapahtuma näytetään myös valitun ajanjakson ulkopuolelta. Erillishaku ei varmista sen päällekkäisyyksiä.</p>
          {selectedEvent.is_draft && <ApprovalButton disabled={selection.refreshing} testid={`selected-event-approve-${selectedEvent.id}`} action="calendar_mutation" resourceId={selectedEvent.id}
            triggerLabel="Lisää Google Kalenteriin" confirmLabel="Lisää tapahtuma" title="Vahvista Google-kalenteriin lisääminen"
            description={`Tapahtuma "${selectedEvent.title}" lisätään Google Kalenteriisi. Tämä muuttaa oikeaa kalenteriasi.`}
            perform={async token=>{await publishEvent(selectedEvent.id,token);toast.success("Tapahtuma lisättiin Google Kalenteriin.");await Promise.all([selection.refetch(),refetch(),refreshOverview()]);}} />}
        </div>}
      </LinkedRecord>
      {data && !loading && <CalendarTimeline events={data} range={range} complete={!agenda.scope.has_more&&!agenda.scope.scan_limited} verified={(integrations||[]).find(row=>row.id==="int-gcal")?.sync_complete===true&&integrationFreshness((integrations||[]).find(row=>row.id==="int-gcal"),freshnessNow)==="fresh"} onSelect={setFocusedId} onRangeEdge={day=>{const next={start:day,end:shiftCalendarDate(day,6)};setRange(next);setRangeDraft(next);setFocusedId(null);}}/>}
      <details className="rounded-xl border border-border p-3"><summary className="min-h-11 cursor-pointer text-sm">Etsi tapahtuma</summary>
      <ContentSearch value={query} onChange={setQuery} label="Etsi ladatuista kalenteritapahtumista" />
      </details>
      <RefreshNotice refreshing={refreshing} error={data ? error : null} updatedAt={updatedAt} onRetry={refetch} />
      {loading ? <LoadingCards /> : error && !data ? <ErrorState error={error} onRetry={refetch} /> : (focusedId || query) && (
        eventGroups.length === 0 ? <EmptyState icon={Calendar} title={query ? "Ei hakutuloksia ladatuissa tapahtumissa" : selection.data ? "Ei muita tapahtumia valitun jakson haetuissa tiedoissa" : "Ei tapahtumia valitun jakson haetuissa tiedoissa"} hint={query ? "Kokeile toista hakusanaa tai tyhjennä haku." : "Voit vaihtaa ajanjaksoa tai hakea kalenterin uusimmat tiedot."} /> : (
          <div className="space-y-6" data-testid="calendar-events">
            {eventGroups.map((group) => (
              <details key={group.id} open={group.id !== "past"} aria-labelledby={`calendar-group-${group.id}`}>
                <summary className="mb-2 flex min-h-11 cursor-pointer items-center justify-between">
                  <h2 id={`calendar-group-${group.id}`} className="text-sm font-semibold text-foreground">{group.label}</h2>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{group.events.length}{group.id === "past" ? " · Avaa/sulje" : ""}</span>
                </summary>
                <div className="space-y-3">
                {group.events.map((e) => (
              <div key={e.id} className={cn("rounded-2xl border bg-card p-4 fade-up", e.conflict ? "border-amber-500/40" : "border-border")} data-testid={`event-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="text-center min-w-[52px]">
                      <p className="font-mono text-sm font-semibold text-primary">{e.all_day ? "Koko päivä" : fiTime(e.start)}</p>
                      {!e.all_day && <p className="font-mono text-[10px] text-muted-foreground">{fiTime(e.end)}</p>}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{e.title}</p>
                      <p className="text-[11px] text-muted-foreground">{fiDate(e.start)}</p>
                    </div>
                  </div>
                  {e.is_draft && <StatusBadge status={e.approval || "Odottaa hyväksyntää"} />}
                </div>

                {e.conflict && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
                    <AlertTriangle className="h-4 w-4" /> Päällekkäisyys tunnistettu
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {e.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location}</span>}
                  {e.attendees?.length > 0 && <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {e.attendees.join(", ")}</span>}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {e.prep_notes?.length > 0 && (
                    <button type="button" data-testid={`event-prep-${e.id}`} onClick={() => setPrep(prep === e.id ? null : e.id)} aria-expanded={prep === e.id}
                            className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted">
                      <FileText className="h-3 w-3" /> Kokouksen valmistelu
                    </button>
                  )}
                  {e.is_draft && (
                    <ApprovalButton
                      testid={`event-approve-${e.id}`}
                      action="calendar_mutation"
                      resourceId={e.id}
                      triggerLabel="Lisää Google Kalenteriin"
                      confirmLabel="Lisää tapahtuma"
                      title="Vahvista Google-kalenteriin lisääminen"
                      description={`Tapahtuma "${e.title}" lisätään Google Kalenteriisi.\n\nMuistutus: ${e.reminder_minutes ?? 30} minuuttia ennen. Tämä muuttaa oikeaa kalenteriasi.`}
                      className="px-3 py-1.5 text-xs"
                      perform={async (token) => { await publishEvent(e.id, token); toast.success("Tapahtuma lisättiin Google Kalenteriin."); await refetch(); await refreshOverview(); }}
                    />
                  )}
                </div>

                {prep === e.id && (
                  <ul className="mt-3 space-y-1 border-t border-border pt-3" data-testid={`prep-notes-${e.id}`}>
                    {e.prep_notes.map((n, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-primary mt-1">•</span>{n}</li>
                    ))}
                  </ul>
                )}
              </div>
                ))}
                </div>
              </details>
            ))}
          </div>
        ))}
    </div>
  );
}
